import type { ReactNode } from 'react';

/**
 * Inline SVG icons.
 *
 * Kept in-repo rather than pulled from an icon dependency, so the client bundle
 * stays small. Each icon inherits `currentColor` and accepts a className.
 */
export interface IconProps {
  className?: string;
}

const BASE = 'h-5 w-5';

function svg(className: string | undefined, children: ReactNode, filled = false) {
  return (
    <svg
      className={className ?? BASE}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function LogoMark({ className }: IconProps) {
  return (
    <svg className={className ?? 'h-8 w-8'} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="ita6-logo" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6366f1" />
          <stop offset="1" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="14" fill="url(#ita6-logo)" />
      <path
        d="M32 12l16 9v18l-16 9-16-9V21z"
        fill="none"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinejoin="round"
      />
      <text
        x="32"
        y="40"
        textAnchor="middle"
        fontFamily="Segoe UI, Arial, sans-serif"
        fontSize="20"
        fontWeight="700"
        fill="#fff"
      >
        6
      </text>
    </svg>
  );
}

export function MenuIcon({ className }: IconProps) {
  return svg(className, <path d="M4 7h16M4 12h16M4 17h16" />);
}

export function CloseIcon({ className }: IconProps) {
  return svg(className, <path d="M6 6l12 12M18 6L6 18" />);
}

export function ArrowRightIcon({ className }: IconProps) {
  return svg(className, <path d="M5 12h14M13 6l6 6-6 6" />);
}

export function UserIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
      <path d="M4 20a8 8 0 0 1 16 0" />
    </>,
  );
}

export function ShieldIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" />
      <path d="M9.5 12.5l1.75 1.75L15 10.5" />
    </>,
  );
}

export function LogOutIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 17l-5-5 5-5M5 12h10" />
    </>,
  );
}

export function ActivityIcon({ className }: IconProps) {
  return svg(className, <path d="M3 12h4l2.5-7 4 14L16 12h5" />);
}

export function UsersIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <path d="M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
      <path d="M2.5 19a6.5 6.5 0 0 1 13 0" />
      <path d="M16 4.5a3.5 3.5 0 0 1 0 7M17.5 19a6.5 6.5 0 0 0-2-4.6" />
    </>,
  );
}

export function TrophyIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" />
      <path d="M8 5H5.5a2.5 2.5 0 0 0 2.5 5M16 5h2.5a2.5 2.5 0 0 1-2.5 5" />
      <path d="M12 13v3M9 20h6M10 20l.5-4h3l.5 4" />
    </>,
  );
}

export function SparklesIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3Z" />
      <path d="M18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" />
    </>,
  );
}

export function AlertIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <path d="M12 4l9 16H3l9-16Z" />
      <path d="M12 10v4M12 17.5v.5" />
    </>,
  );
}

export function CheckCircleIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.5l2.25 2.25L15.5 10" />
    </>,
  );
}

export function DatabaseIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v6c0 1.66 3.13 3 7 3s7-1.34 7-3V6" />
      <path d="M5 12v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6" />
    </>,
  );
}

export function ClockIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 2" />
    </>,
  );
}

export function MailIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 7l8.5 6 8.5-6" />
    </>,
  );
}

export function SunIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>,
  );
}

export function MoonIcon({ className }: IconProps) {
  return svg(className, <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />);
}

export function MonitorIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>,
  );
}

export function GlobeIcon({ className }: IconProps) {
  return svg(
    className,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.7 3.75 5.7 3.75 9S14.5 18.3 12 21c-2.5-2.7-3.75-5.7-3.75-9S9.5 5.7 12 3Z" />
    </>,
  );
}

export function CheckIcon({ className }: IconProps) {
  return svg(className, <path d="M5 12.5l4.5 4.5L19 7.5" />);
}
